import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ArrowLeft, Eye, EyeOff, CheckCircle, Clock, Users, User, Globe, Lock } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PrivacySettingsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    show_read_receipts: true,
    last_seen_visibility: 'everyone',
    online_status_visibility: 'everyone',
    profile_photo_visibility: 'everyone'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/users/privacy-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch privacy settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (newSettings) => {
    setSaving(true);
    try {
      await axios.put(`${API}/users/privacy-settings`, newSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(newSettings);
      toast.success('Privacy settings updated');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (key) => {
    const newSettings = { ...settings, [key]: !settings[key] };
    saveSettings(newSettings);
  };

  const handleSelect = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    saveSettings(newSettings);
  };

  const visibilityOptions = [
    { value: 'everyone', label: 'Everyone', icon: Globe },
    { value: 'chats', label: 'People I\'ve Chatted With', icon: Users },
    { value: 'nobody', label: 'Nobody', icon: Lock }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background-DEFAULT flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-DEFAULT flex flex-col" data-testid="privacy-settings-page">
      <div className="p-6 border-b border-border-DEFAULT">
        <div className="chat-container flex items-center gap-4">
          <button
            data-testid="back-btn"
            onClick={() => navigate('/settings')}
            className="h-10 w-10 rounded-full flex items-center justify-center hover:bg-secondary-DEFAULT transition-colors"
          >
            <ArrowLeft size={20} className="text-text-primary" />
          </button>
          <h1 className="font-heading text-2xl text-text-primary">Privacy Settings</h1>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide p-6">
        <div className="chat-container max-w-2xl mx-auto space-y-6">
          
          {/* Read Receipts */}
          <div className="bg-white rounded-3xl p-6 shadow-card border border-border-DEFAULT">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <CheckCircle size={24} className="text-blue-500" />
                </div>
                <div>
                  <h3 className="font-medium text-text-primary">Read Receipts</h3>
                  <p className="text-sm text-text-secondary">
                    {settings.show_read_receipts 
                      ? "Others can see when you've read their messages" 
                      : "Your read status is hidden from others"}
                  </p>
                </div>
              </div>
              <button
                data-testid="read-receipts-toggle"
                onClick={() => handleToggle('show_read_receipts')}
                disabled={saving}
                className={`w-14 h-8 rounded-full transition-colors relative ${
                  settings.show_read_receipts ? 'bg-primary' : 'bg-gray-300'
                }`}
              >
                <div className={`w-6 h-6 bg-white rounded-full absolute top-1 transition-all ${
                  settings.show_read_receipts ? 'right-1' : 'left-1'
                }`} />
              </button>
            </div>
            <p className="text-xs text-text-tertiary mt-4 pl-16">
              If turned off, you also won't be able to see others' read receipts.
            </p>
          </div>

          {/* Last Seen */}
          <div className="bg-white rounded-3xl p-6 shadow-card border border-border-DEFAULT">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <Clock size={24} className="text-green-500" />
              </div>
              <div>
                <h3 className="font-medium text-text-primary">Last Seen</h3>
                <p className="text-sm text-text-secondary">Who can see when you were last online</p>
              </div>
            </div>
            <div className="space-y-2 ml-16">
              {visibilityOptions.map((option) => (
                <button
                  key={option.value}
                  data-testid={`last-seen-${option.value}`}
                  onClick={() => handleSelect('last_seen_visibility', option.value)}
                  disabled={saving}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${
                    settings.last_seen_visibility === option.value 
                      ? 'bg-primary/10 border-2 border-primary' 
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <option.icon size={18} className={settings.last_seen_visibility === option.value ? 'text-primary' : 'text-text-secondary'} />
                  <span className={settings.last_seen_visibility === option.value ? 'text-primary font-medium' : 'text-text-primary'}>
                    {option.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Online Status */}
          <div className="bg-white rounded-3xl p-6 shadow-card border border-border-DEFAULT">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <div className="w-4 h-4 bg-emerald-500 rounded-full" />
              </div>
              <div>
                <h3 className="font-medium text-text-primary">Online Status</h3>
                <p className="text-sm text-text-secondary">Who can see when you're online</p>
              </div>
            </div>
            <div className="space-y-2 ml-16">
              {visibilityOptions.map((option) => (
                <button
                  key={option.value}
                  data-testid={`online-status-${option.value}`}
                  onClick={() => handleSelect('online_status_visibility', option.value)}
                  disabled={saving}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${
                    settings.online_status_visibility === option.value 
                      ? 'bg-primary/10 border-2 border-primary' 
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <option.icon size={18} className={settings.online_status_visibility === option.value ? 'text-primary' : 'text-text-secondary'} />
                  <span className={settings.online_status_visibility === option.value ? 'text-primary font-medium' : 'text-text-primary'}>
                    {option.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Profile Photo */}
          <div className="bg-white rounded-3xl p-6 shadow-card border border-border-DEFAULT">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <User size={24} className="text-purple-500" />
              </div>
              <div>
                <h3 className="font-medium text-text-primary">Profile Photo</h3>
                <p className="text-sm text-text-secondary">Who can see your profile photo</p>
              </div>
            </div>
            <div className="space-y-2 ml-16">
              {visibilityOptions.map((option) => (
                <button
                  key={option.value}
                  data-testid={`profile-photo-${option.value}`}
                  onClick={() => handleSelect('profile_photo_visibility', option.value)}
                  disabled={saving}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${
                    settings.profile_photo_visibility === option.value 
                      ? 'bg-primary/10 border-2 border-primary' 
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <option.icon size={18} className={settings.profile_photo_visibility === option.value ? 'text-primary' : 'text-text-secondary'} />
                  <span className={settings.profile_photo_visibility === option.value ? 'text-primary font-medium' : 'text-text-primary'}>
                    {option.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-yellow-50 rounded-3xl p-6 border border-yellow-200">
            <div className="flex items-start gap-3">
              <EyeOff size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-yellow-800">Privacy Note</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  If you hide your information from others, you also won't be able to see their information. 
                  This ensures mutual privacy for all users.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacySettingsPage;
